<script setup lang="ts">
import { GlobeIcon } from '@blpde/nx-icons';
import { computed, useTemplateRef, type InputHTMLAttributes } from 'vue';
import type { BaseTextInputProps, InlineIconProps } from './internal';
import NxInput from './NxInput.vue';

// A text input specialised for URLs: it looks and behaves exactly like
// `NxTextInput`, but bakes in a trailing "open link" icon. Clicking the icon
// opens the entered URL in a new tab. The stored value is never rewritten — a
// missing scheme is only prefixed on the opened href (non-mutating), so a bare
// domain like "blp.de" opens as an external link instead of a relative path.
const props = withDefaults(
  defineProps<
    BaseTextInputProps & {
      /** Accessible label / tooltip for the trailing open-link icon button. */
      openLabel?: string;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>
  >(),
  { openLabel: 'Open link' }
);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

const hasValue = computed(() => (valueModel.value ?? '').trim().length > 0);

function openLink() {
  const trimmed = (valueModel.value ?? '').trim();
  if (!trimmed) return;
  const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  window.open(href, '_blank', 'noopener');
}

// Built-in trailing icon. With a value it is a brand-coloured clickable button;
// when empty we drop `onClick`, so NxInputWrapper renders it as an inert, greyed
// <span> instead of a button — visibly present but not clickable.
const linkIcons = computed<InlineIconProps[]>(() => [
  {
    component: GlobeIcon,
    align: 'end',
    filled: true,
    color: hasValue.value ? 'var(--nxt-brand-bg)' : 'var(--nxt-fg-muted)',
    label: props.openLabel,
    onClick: hasValue.value ? () => openLink() : undefined,
    deco: false,
  },
]);

const inputRef = useTemplateRef('input-element');
const exposeRef = computed(() => {
  return inputRef.value?.inputElement;
});
defineExpose({
  inputElement: exposeRef,
});
</script>

<template>
  <NxInput
    v-if="valueModelModifiers.lazy"
    :id="$props.id"
    ref="input-element"
    v-model.lazy="valueModel"
    type="text"
    inputmode="url"
    :icons="linkIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
  />
  <NxInput
    v-else
    :id="$props.id"
    ref="input-element"
    v-model="valueModel"
    type="text"
    inputmode="url"
    :icons="linkIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
  />
</template>
